import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiCopy, 
  FiEye, 
  FiSave,
  FiX,
  FiAlertCircle
} from 'react-icons/fi';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import YAMLTemplateEditor from '../components/YAMLTemplateEditor';
import ExcelColumnMapper from '../components/ExcelColumnMapper';

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api/";

const CommentTemplateManagementPage = () => {
  const { token, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [yamlIsValid, setYamlIsValid] = useState(true);
  const [columnMapping, setColumnMapping] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    grade_level: '',
    subject: '',
    yaml_structure: '',
    ai_preamble: '',
    ai_postamble: '',
    is_shared: false
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Memoized onChange handlers to prevent unnecessary re-renders
  const handleYamlChange = useCallback((yamlValue) => {
    console.log('CommentTemplateManagementPage - handleYamlChange called with:', yamlValue);
    setFormData(prev => ({...prev, yaml_structure: yamlValue}));
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({...prev, [field]: value}));
  }, []);

  const handleColumnMappingChange = useCallback((mapping) => {
    try {
      setColumnMapping(mapping);
      // Only update YAML if mapping is not empty and has actual data
      if (mapping && Array.isArray(mapping) && mapping.length > 0 && mapping.some(cat => cat.components && cat.components.length > 0)) {
        const learnerDataMap = generateLearnerDataMapYAML(mapping);
        // Use the latest formData value by using the functional setState
        setFormData(prev => {
          const existingYaml = prev.yaml_structure || getDefaultStructure();
          const combinedYaml = combineLearnerDataMapWithYaml(learnerDataMap, existingYaml);
          return {...prev, yaml_structure: combinedYaml};
        });
      }
    } catch (error) {
      console.error('Error handling column mapping change:', error);
    }
  }, []); // Empty dependencies is OK now since we use functional setState

  const generateLearnerDataMapYAML = (mapping) => {
    try {
      console.log('generateLearnerDataMapYAML - Input mapping:', mapping);
      const learnerDataMap = {
        assessments: {}
      };

      if (!mapping || !Array.isArray(mapping)) {
        console.log('generateLearnerDataMapYAML - Invalid mapping, returning empty');
        return learnerDataMap;
      }

      mapping.forEach(category => {
        if (category && category.name) {
          learnerDataMap.assessments[category.name] = {
            category_name: category.displayName || category.name,
            components: {}
          };

          if (category.components && Array.isArray(category.components)) {
            category.components.forEach(component => {
              if (component && component.name) {
                learnerDataMap.assessments[category.name].components[component.name] = {
                  column_index: component.columnIndex || 0,
                  max_marks: component.maxMarks || 50,
                  description: component.description || 'Assessment component'
                };
              }
            });
          }
        }
      });

      console.log('generateLearnerDataMapYAML - Generated learnerDataMap:', learnerDataMap);
      return learnerDataMap;
    } catch (error) {
      console.error('Error generating learner data map YAML:', error);
      return { assessments: {} };
    }
  };

  const combineLearnerDataMapWithYaml = (learnerDataMap, existingYaml) => {
    const learnerDataMapYaml = `learner_data_map:\n${formatYAMLObject(learnerDataMap, 1)}\n`;
    
    console.log('Combining learner data map with YAML');
    console.log('Learner data map YAML:', learnerDataMapYaml);
    console.log('Existing YAML:', existingYaml);
    
    // If existing YAML doesn't have comment_sections, add default
    if (!existingYaml.includes('comment_sections:')) {
      existingYaml = getDefaultStructure();
    }
    
    const combined = learnerDataMapYaml + existingYaml;
    console.log('Combined YAML result:', combined);
    
    return combined;
  };

  const formatYAMLObject = (obj, indent = 0) => {
    const spaces = '  '.repeat(indent);
    let yaml = '';
    
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'object' && value !== null) {
        yaml += `${spaces}${key}:\n${formatYAMLObject(value, indent + 1)}`;
      } else {
        yaml += `${spaces}${key}: ${value}\n`;
      }
    }
    
    return yaml;
  };

  const parseYAMLToColumnMapping = (yamlString) => {
    if (!yamlString || !yamlString.includes('learner_data_map:')) {
      return [];
    }

    try {
      // Simple YAML parsing for learner_data_map
      const lines = yamlString.split('\n');
      const mapping = [];
      let currentCategory = null;
      let inLearnerDataMap = false;
      let inAssessments = false;

      for (const line of lines) {
        const trimmed = line.trim();
        
        if (trimmed === 'learner_data_map:') {
          inLearnerDataMap = true;
          continue;
        }
        
        if (inLearnerDataMap && trimmed === 'assessments:') {
          inAssessments = true;
          continue;
        }
        
        if (inAssessments && trimmed.includes(':') && !trimmed.includes('category_name:') && !trimmed.includes('components:')) {
          // New category
          const categoryName = trimmed.split(':')[0];
          currentCategory = {
            id: Date.now() + Math.random(),
            name: categoryName,
            displayName: categoryName,
            components: []
          };
          mapping.push(currentCategory);
        }
        
        if (currentCategory && trimmed.includes('category_name:')) {
          const displayName = trimmed.split('category_name:')[1].trim().replace(/['"]/g, '');
          currentCategory.displayName = displayName;
        }
        
        if (currentCategory && trimmed.includes('column_index:')) {
          const columnIndex = parseInt(trimmed.split('column_index:')[1].trim());
          const component = {
            id: Date.now() + Math.random(),
            name: 'component',
            displayName: 'Component',
            columnIndex: columnIndex,
            maxMarks: 50,
            description: 'Assessment component'
          };
          currentCategory.components.push(component);
        }
      }

      return mapping;
    } catch (error) {
      console.error('Error parsing YAML to column mapping:', error);
      return [];
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}comment-templates/`, {
        headers: { Authorization: `Token ${token}` }
      });
      setTemplates(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Preserve existing learner_data_map if editing and no new mapping provided
      let finalYamlStructure = formData.yaml_structure || getDefaultStructure();
      
      console.log('Form submission - Current YAML structure:', finalYamlStructure);
      console.log('Form submission - Current column mapping:', columnMapping);
      console.log('Form submission - Has learner_data_map:', finalYamlStructure.includes('learner_data_map:'));
      
      if (editingTemplate && editingTemplate.yaml_structure.includes('learner_data_map:')) {
        // Check if the current YAML doesn't have learner_data_map
        if (!finalYamlStructure.includes('learner_data_map:')) {
          console.log('Preserving learner_data_map from original template');
          // Extract learner_data_map from original template
          const originalLines = editingTemplate.yaml_structure.split('\n');
          const yamlLines = finalYamlStructure.split('\n');
          let learnerDataMapSection = [];
          let inLearnerDataMap = false;
          
          // Extract learner_data_map section from original
          for (const line of originalLines) {
            if (line.trim() === 'learner_data_map:') {
              inLearnerDataMap = true;
            }
            if (inLearnerDataMap) {
              learnerDataMapSection.push(line);
              // Stop when we hit the next top-level key
              if (line.trim() && !line.startsWith(' ') && line !== 'learner_data_map:') {
                learnerDataMapSection.pop(); // Remove the line that's not part of learner_data_map
                break;
              }
            }
          }
          
          // Prepend learner_data_map to the YAML
          if (learnerDataMapSection.length > 0) {
            finalYamlStructure = learnerDataMapSection.join('\n') + '\n' + finalYamlStructure;
          }
        }
      }
      
      // Send YAML structure as string (backend will handle parsing)
      const templateData = {
        ...formData,
        yaml_structure: finalYamlStructure
      };

      console.log('Submitting template data:', templateData);
      console.log('Final YAML structure being submitted:', templateData.yaml_structure);

      if (editingTemplate) {
        await axios.put(`${API_URL}comment-templates/${editingTemplate.id}/`, templateData, {
          headers: { Authorization: `Token ${token}` }
        });
      } else {
        await axios.post(`${API_URL}comment-templates/`, templateData, {
          headers: { Authorization: `Token ${token}` }
        });
      }

      setShowModal(false);
      setEditingTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Template submission error:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to save template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      grade_level: template.grade_level,
      subject: template.subject,
      yaml_structure: template.yaml_structure || getDefaultStructure(),
      ai_preamble: template.ai_preamble,
      ai_postamble: template.ai_postamble,
      is_shared: template.is_shared
    });
    
    // Try to parse existing column mapping from YAML
    try {
      const parsedMapping = parseYAMLToColumnMapping(template.yaml_structure);
      setColumnMapping(parsedMapping);
    } catch (error) {
      console.error('Error parsing column mapping from YAML:', error);
      setColumnMapping([]);
    }
    
    setShowModal(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}comment-templates/${templateId}/`, {
        headers: { Authorization: `Token ${token}` }
      });
      fetchTemplates();
    } catch (error) {
      setError('Failed to delete template');
    }
  };

  const handleDuplicate = async (templateId) => {
    try {
      await axios.post(`${API_URL}comment-templates/${templateId}/duplicate/`, {}, {
        headers: { Authorization: `Token ${token}` }
      });
      fetchTemplates();
    } catch (error) {
      setError('Failed to duplicate template');
    }
  };

  const resetForm = () => {
    console.log('resetForm called');
    setFormData({
      name: '',
      description: '',
      grade_level: '',
      subject: '',
      yaml_structure: '',
      ai_preamble: '',
      ai_postamble: '',
      is_shared: false
    });
  };

  const handleNewTemplate = () => {
    console.log('handleNewTemplate called');
    setEditingTemplate(null);
    resetForm();
    // Set default YAML structure for new templates
    setFormData(prevData => ({
      ...prevData,
      yaml_structure: getDefaultStructure()
    }));
    // Reset column mapping for new templates
    setColumnMapping([]);
    console.log('Setting showModal to true');
    setShowModal(true);
  };

  const getDefaultStructure = () => {
    return `comment_sections:
  performance_overview:
    title: "Performance Overview"
    subtitle: "Overall assessment performance"
    bullet_points: 2
    analysis_type: "performance_analysis"
  strengths:
    title: "Strengths"
    subtitle: "Areas of excellence"
    bullet_points: 2
    analysis_type: "performance_analysis"
  improvement_areas:
    title: "Areas for Improvement"
    subtitle: "Skills requiring development"
    bullet_points: 2
    analysis_type: "improvement_recommendations"
  encouragement:
    title: "Encouragement"
    subtitle: "Personal motivation"
    include_name: true
    analysis_type: "personal_encouragement"`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Comment Templates</h1>
            <p className="text-gray-600">Manage your comment generation templates</p>
          </div>
          <button
            onClick={handleNewTemplate}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <FiPlus className="w-4 h-4 mr-2" />
            New Template
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <FiAlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm">
          <div className="divide-y divide-gray-200">
            {templates.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FiEdit2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No templates found. Create your first template to get started.</p>
              </div>
            ) : (
              templates.map(template => (
                <div key={template.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{template.name}</h3>
                      <p className="text-gray-600 mb-2">{template.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {template.grade_level}
                        </span>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                          {template.subject}
                        </span>
                        {template.is_shared && (
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                            Shared
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPreviewTemplate(template)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="Preview Structure"
                      >
                        <FiEye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(template.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="Duplicate Template"
                      >
                        <FiCopy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="Edit Template"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete Template"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Template Form Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">
                  {editingTemplate ? 'Edit Template' : 'New Template'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template Name
                      {editingTemplate && editingTemplate.created_by?.user?.id !== user?.id && (
                        <span className="text-xs text-gray-500 ml-2">(Read-only - owned by {editingTemplate.created_by?.user?.first_name} {editingTemplate.created_by?.user?.last_name})</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                      disabled={editingTemplate && editingTemplate.created_by?.user?.id !== user?.id}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Grade Level
                    </label>
                    <input
                      type="text"
                      value={formData.grade_level}
                      onChange={(e) => handleFormChange('grade_level', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Grade 9"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => handleFormChange('subject', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., CAT, IT, Mathematics"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe when and how to use this template"
                  />
                </div>

                <div>
                  <ExcelColumnMapper
                    onMappingChange={handleColumnMappingChange}
                    initialMapping={columnMapping}
                  />
                </div>

                <div>
                  {console.log('Passing to YAMLTemplateEditor - value:', formData.yaml_structure)}
                  <YAMLTemplateEditor
                    value={formData.yaml_structure}
                    onChange={handleYamlChange}
                    onValidationChange={setYamlIsValid}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AI Preamble
                  </label>
                  <textarea
                    value={formData.ai_preamble}
                    onChange={(e) => handleFormChange('ai_preamble', e.target.value)}
                    rows={4}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Instructions to prepend to AI prompt..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AI Postamble
                  </label>
                  <textarea
                    value={formData.ai_postamble}
                    onChange={(e) => handleFormChange('ai_postamble', e.target.value)}
                    rows={4}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Instructions to append to AI prompt..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_shared"
                    checked={formData.is_shared}
                    onChange={(e) => handleFormChange('is_shared', e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="is_shared" className="text-sm text-gray-700">
                    Share with other teachers in my school
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!yamlIsValid}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiSave className="w-4 h-4 mr-2" />
                    {editingTemplate ? 'Update' : 'Create'} Template
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {previewTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Template Preview: {previewTemplate.name}</h2>
                <button
                  onClick={() => setPreviewTemplate(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Grade Level:</span> {previewTemplate.grade_level}
                  </div>
                  <div>
                    <span className="font-medium">Subject:</span> {previewTemplate.subject}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium mb-2">Comment Sections:</h3>
                  <div className="space-y-3">
                    {Object.entries(previewTemplate.yaml_structure.comment_sections || {}).map(([key, section]) => (
                      <div key={key} className="border-l-4 border-blue-500 pl-4 py-2 bg-gray-50">
                        <h4 className="font-medium">{section.title}</h4>
                        {section.subtitle && (
                          <p className="text-sm text-gray-600 italic">{section.subtitle}</p>
                        )}
                        <div className="text-sm text-gray-500 mt-1">
                          Type: {section.analysis_type} | 
                          Bullets: {section.bullet_points || 'N/A'} |
                          Include Name: {section.include_name ? 'Yes' : 'No'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentTemplateManagementPage;